import type { CommandRequest, GameGateway, PresenceState, Unsubscribe } from '@mendikot/app-core';
import type { CommandMeta, Reply, RoomPrivateView, RoomPublicView } from '@mendikot/protocol';
import { loadOnline } from './firebase';
import type { FirebaseGameGateway } from './gateway';

/**
 * GameGateway facade that loads the Firebase Database/Functions SDKs and the real gateway on first
 * use. Subscriptions requested before the SDK is ready are attached as soon as it is.
 */
export class LazyFirebaseGateway implements GameGateway {
  readonly kind = 'online' as const;
  private impl: FirebaseGameGateway | null = null;
  private ready: Promise<FirebaseGameGateway> | null = null;

  private load(): Promise<FirebaseGameGateway> {
    this.ready ??= Promise.all([loadOnline(), import('./gateway')]).then(([handles, mod]) => {
      this.impl = new mod.FirebaseGameGateway(handles.db, handles.functions);
      return this.impl;
    });
    return this.ready;
  }

  private defer(attach: (impl: FirebaseGameGateway) => Unsubscribe): Unsubscribe {
    let detach: Unsubscribe | null = null;
    let cancelled = false;
    void this.load().then((impl) => {
      if (!cancelled) detach = attach(impl);
    });
    return () => {
      cancelled = true;
      detach?.();
    };
  }

  async send(request: CommandRequest, meta: CommandMeta): Promise<Reply> {
    return (await this.load()).send(request, meta);
  }

  subscribePublic(
    roomId: string,
    listener: (v: RoomPublicView | null) => void,
    onError?: (e: unknown) => void,
  ) {
    return this.defer((impl) => impl.subscribePublic(roomId, listener, onError));
  }

  subscribePrivate(
    roomId: string,
    seat: number,
    listener: (v: RoomPrivateView | null) => void,
    onError?: (e: unknown) => void,
  ) {
    return this.defer((impl) => impl.subscribePrivate(roomId, seat, listener, onError));
  }

  watchPresence(roomId: string, listener: (p: Record<string, PresenceState>) => void) {
    return this.defer((impl) => impl.watchPresence(roomId, listener));
  }

  joinPresence(roomId: string, uid: string) {
    return this.defer((impl) => impl.joinPresence(roomId, uid));
  }

  setVisible(roomId: string, uid: string, visible: boolean): void {
    void this.load().then((impl) => impl.setVisible(roomId, uid, visible));
  }

  onConnectionChange(listener: (connected: boolean) => void) {
    return this.defer((impl) => impl.onConnectionChange(listener));
  }

  serverNow(): number {
    return this.impl?.serverNow() ?? Date.now();
  }
}
